import {
  React,
  useState,
  classNames,
  useEffect,
  useCallback,
} from '@digsup/core-ui';
import PropTypes from 'prop-types';
import GoogleMapReact from 'google-map-react';
import { geocodeByPlaceId, getLatLng } from 'react-places-autocomplete';
import Geocode from 'react-geocode';
import { Marker } from './Marker';
import { AddressSearchBar } from './AddressSearchBar';
import { TypeControl } from './TypeControl';
import { MapControls } from './MapControls';
import { createMapOptions } from './helpers/createMapOptions';
import { smoothlyAnimatePanTo } from './helpers/smoothlyAnimatePanTo';
import './Map.scss';

const cx = classNames;

export const Map = ({
  apiKey,
  lat,
  lng,
  isFillParent,
  onChange,
  className,
  children,
  onDrag,
  onDragEnd,
  googlePlaceId,
  smartyStreetsApiKey,
  searchbarInputValue,
  ...props
}) => {
  Geocode.setApiKey(apiKey);
  const [searchValue, setSearchValue] = useState(searchbarInputValue);
  const [mapState, setMapState] = useState({
    map: null,
    maps: null,
    loaded: false,
    type: 'HYBRID',
    isStreetViewAvailable: true,
  });

  const [markerPosition, setMarkerPosition] = useState({
    lat,
    lng,
  });

  const classes = cx('c-map', className, {
    'c-map--fill-parent': isFillParent,
  });

  const moveTo = useCallback(
    (lt, lg) => {
      smoothlyAnimatePanTo(mapState.map, new google.maps.LatLng(lt, lg));

      setMarkerPosition({
        ...markerPosition,
        lat: lt,
        lng: lg,
      });

      const streetViewService = new google.maps.StreetViewService();
      streetViewService
        .getPanorama({ location: { lat: lt, lng: lg } })
        .then(({ data }) => {
          if (data?.imageDate) {
            setMapState({
              ...mapState,
              isStreetViewAvailable: true,
            });
          }
        })
        .catch(() =>
          setMapState({
            ...mapState,
            isStreetViewAvailable: false,
          }),
        );
    },
    [mapState, markerPosition],
  );

  const handleMapClick = args => {
    Geocode.fromLatLng(args.lat, args.lng).then(
      response => {
        const res = response.results[0];
        const addressDetails = {
          formattedAddress: res?.formatted_address,
        };
        setSearchValue(res?.formatted_address);
        moveTo(args.lat, args.lng);
        onChange(res.place_id, addressDetails);
      },
      error => {
        console.error(error);
      },
    );
  };

  const onSelect = useCallback(
    (placeId, addressDetails) => {
      let newAddress = {};
      geocodeByPlaceId(placeId)
        .then(results => {
          newAddress = {
            ...addressDetails,
            formattedAddress: results?.[0]?.formatted_address,
          };
          return getLatLng(results[0]);
        })
        .then(res => {
          moveTo(res.lat, res.lng);
          onChange(placeId, newAddress);
        })
        .catch(error => console.error(error));
    },
    [moveTo, onChange],
  );

  const setMapType = type => {
    const panorama = mapState.map.getStreetView();
    if (type === 'STREET') {
      panorama.setOptions({
        fullscreenControl: false,
        enableCloseButton: false,
      });
      panorama.setPosition(markerPosition);
      panorama.setPov({
        heading: 265,
        pitch: 0,
      });

      setMapState({
        ...mapState,
        type,
      });

      panorama.setVisible(true);
    } else {
      if (mapState.type === 'STREET') {
        panorama.setVisible(false);
      }

      mapState.map.setOptions({
        mapTypeId: mapState.maps.MapTypeId[type],
      });

      setMapState({
        ...mapState,
        type,
      });
    }
  };

  const onChildMouseDown = () => {
    setMapState({
      ...mapState,
      draggable: false,
    });
  };

  const onChildMouseUp = () => {
    setMapState({
      ...mapState,
      draggable: true,
    });

    handleMapClick(markerPosition);
  };

  const onChildMouseMove = (key, marker, newCoords) => {
    setMarkerPosition(newCoords);
  };

  useEffect(() => {
    if (googlePlaceId && mapState.loaded) {
      onSelect(googlePlaceId);
    }
  }, [googlePlaceId, mapState.loaded, onSelect]);

  useEffect(() => {
    if (searchbarInputValue) {
      setSearchValue(searchbarInputValue);
    }
  }, [searchbarInputValue]);

  return (
    <div className={classes} {...props}>
      {mapState.loaded && window?.google && (
        <AddressSearchBar
          smartyStreetsApiKey={smartyStreetsApiKey}
          className="c-map__search-bar"
          value={searchValue}
          onSelect={onSelect}
        />
      )}
      <GoogleMapReact
        onClick={handleMapClick}
        bootstrapURLKeys={{
          key: apiKey,
          libraries: 'places',
        }}
        defaultCenter={{
          lat,
          lng,
        }}
        defaultZoom={12}
        yesIWantToUseGoogleMapApiInternals
        options={createMapOptions}
        onChildMouseDown={onChildMouseDown}
        onChildMouseUp={onChildMouseUp}
        onChildMouseMove={onChildMouseMove}
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        draggable={mapState.draggable}
        // eslint-disable-next-line react/prop-types
        onGoogleApiLoaded={({ map, maps }) =>
          setMapState({
            ...mapState,
            map,
            maps,
            loaded: true,
          })
        }>
        {mapState.loaded && markerPosition && (
          <Marker
            lat={markerPosition.lat}
            lng={markerPosition.lng}
            isFocused={!mapState.draggable}
          />
        )}
      </GoogleMapReact>
      <TypeControl
        onClick={setMapType}
        current={mapState.type}
        isStreetViewAvailable={mapState.isStreetViewAvailable}
      />
      {mapState.loaded && <MapControls map={mapState.map} />}
      {children}
    </div>
  );
};

Map.propTypes = {
  searchbarInputValue: PropTypes.string,
  apiKey: PropTypes.string.isRequired,
  smartyStreetsApiKey: PropTypes.string,
  lat: PropTypes.number,
  lng: PropTypes.number,
  isFillParent: PropTypes.bool,
  /**
   * Callback when placeId is changed
   */
  onChange: PropTypes.func.isRequired,
  googlePlaceId: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.any,
  onDrag: PropTypes.func,
  onDragEnd: PropTypes.func,
};

Map.defaultProps = {
  lat: 42.361145,
  lng: -71.057083,
  className: '',
  apiKey: process.env.GOOGLE_API_KEY,
};
